import { Genre, Maybe } from 'types/graphql-api.generated';

import { BRAND_HAS_SAME_LOCAL_AND_DUBBED_SHOWTIMES } from 'common/configuration/constants';
import { BOOKING, SHOWTIMES_VERSION } from 'common/constants/Showtimes';
import request from 'common/tools/network/request';
import { path } from 'common/tools/network/routing';
import { getVersionGroupKey } from 'common/tools/showtime/getVersionLabel';
import slugify from 'common/tools/string/slugify';
// import trans from 'common/tools/translations/trans'; // used by the commented-out block below
import { isString } from 'common/types';

import { MovieFromRequest } from 'website/containers/showtimes/types';
import {
  Filter,
  FormatedFilters,
  PartialShowtime,
  RequestPayload,
  RequestShowtimesData,
  RouteParam,
  ShowtimesFiltersFacets
} from 'website/containers/showtimes/utils/types';

const getFilters = (filters: FormatedFilters): string[] => {
  return filters.flatMap(filter =>
    Object.values(filter).flatMap(value =>
      Array.isArray(value) ? value.filter(isString) : []
    )
  );
};

const buildHash = (
  routeParam: Pick<RouteParam, 'day' | 'page'>,
  payload: Pick<RequestPayload, 'filters'>
) => {
  let hash = '#';

  //Usefull for jad previews hash in URL
  if (window.location.hash) {
    const splitHash = window.location.hash.split('#')[1].split('&');
    const primaryKeys = ['shwt_date', 'page', 'filters'];

    const tmpHash = splitHash.reduce((result: string[], item: string) => {
      const isKeyPresent = primaryKeys.some(key => item.includes(key));
      if (!isKeyPresent) {
        result.push(item);
      }
      return result;
    }, []);

    if (tmpHash.length) {
      hash += tmpHash.join('&') + '&';
    }
  }

  if (routeParam.day) {
    hash += `shwt_date=${routeParam.day}`;
  }

  if (routeParam.page !== 1) {
    hash += `&page=${routeParam.page}`;
  }

  const filters = getFilters(payload.filters ?? []);

  if (filters.length) {
    hash += `&filters=${filters.map(key => slugify(key)).join('+')}`;
  }

  return hash;
};

export const addToHistory = (
  routeParam: Pick<RouteParam, 'day' | 'page'> = {},
  payload: Pick<RequestPayload, 'filters'> = {}
) => {
  const hash = buildHash(routeParam, payload);
  window.history.pushState(payload, '', hash);
};

export const requestShowtimes = async <
  T extends RequestShowtimesData,
  P extends { [key: string]: any },
  R = Record<string, any>
>(
  routeName: string,
  routeParam: R,
  payload?: P
) => {
  const url = path(routeName, { ...(routeParam ?? {}) });

  return request<T, P>(url, {
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'same-origin',
    method: 'POST',
    payload: payload
  });
};

export const handlePagerHref = (
  routeName: string,
  pageNumber: number,
  payload: RequestPayload,
  routeParam: RouteParam = {}
) => {
  const modifiedRouteParam = { ...routeParam };
  let param = {};
  if (modifiedRouteParam.movie && modifiedRouteParam.localization) {
    param = {
      movie: modifiedRouteParam.movie,
      localization: modifiedRouteParam.localization
    };
  }

  if (modifiedRouteParam.idTheater) {
    param = {
      idTheater: modifiedRouteParam.idTheater
    };
  }

  return `${path(routeName, param)}${buildHash({ ...modifiedRouteParam, page: pageNumber }, payload)}`;
};

// Every ShowtimeVersionDetail value must be filterable on its own (VO, VOST, VO SME,
// VF, VF ST, VF SME…) — St and Sme are never merged together. The only merge is
// Local/Dubbed, and only for brands where they're the same underlying audio track.
export const VERSION_GROUP: Record<string, string> = {
  Local: 'local',
  LocalSt: 'local_st',
  LocalSme: 'local_sme',
  Original: 'original',
  OriginalSt: 'original_st',
  OriginalSme: 'original_sme',
  ...(BRAND_HAS_SAME_LOCAL_AND_DUBBED_SHOWTIMES
    ? {
        // Flag on (e.g. AC): Dubbed is a duplicate of Local for this brand, so it
        // joins the same groups as Local.
        Dubbed: 'local',
        DubbedSt: 'local_st',
        DubbedSme: 'local_sme'
      }
    : {
        // Flag off: Dubbed is a genuinely different audio track, kept in its own
        // groups (not lumped with Local) so it still gets translated rather than
        // falling through to an untranslated raw key.
        Dubbed: 'dubbed',
        DubbedSt: 'dubbed_st',
        DubbedSme: 'dubbed_sme'
      })
};

// Case/underscore-insensitive lookup so a backend casing change (LOCAL_ST vs
// LocalSt) doesn't silently drop the value instead of matching its group.
const normalizeVersionKey = (key: string) =>
  key.replace(/_/g, '').toLowerCase();

const VERSION_GROUP_NORMALIZED: Record<string, string> = Object.fromEntries(
  Object.entries(VERSION_GROUP).map(([key, value]) => [
    normalizeVersionKey(key),
    value
  ])
);

export const versionGroupKeyOf = (key?: string | null) =>
  key ? VERSION_GROUP_NORMALIZED[normalizeVersionKey(key)] : undefined;

// Display order for the version filter dropdown (VF, VF ST, VF SME, VO, VOST, VO SME…),
// independent of the doc_count order the ES aggregation returns.
export const VERSION_GROUP_ORDER = [
  'local',
  'local_st',
  'local_sme',
  ...(BRAND_HAS_SAME_LOCAL_AND_DUBBED_SHOWTIMES
    ? []
    : ['dubbed', 'dubbed_st', 'dubbed_sme']),
  'original',
  'original_st',
  'original_sme'
];

/**
 * This function will toggle the active state in the filter object for the given entityGroup and entityKey.
 * For SHOWTIMES_VERSION, all raw keys sharing the clicked key's VERSION_GROUP are toggled together as one unit.
 */
export const handleShowtimeFilterChange = (
  filters?: Maybe<Filter[]>,
  entityGroup?: string,
  entityKey?: Maybe<string>
) => {
  const filterGroup = filters?.find(filter => filter.name === entityGroup);

  const groupKey = versionGroupKeyOf(entityKey);
  const keysToToggle =
    entityGroup === SHOWTIMES_VERSION && groupKey
      ? filterGroup?.values
          ?.filter(entity => versionGroupKeyOf(entity?.key) === groupKey)
          .map(entity => entity?.key as string) ?? [entityKey]
      : [entityKey];

  const matchedEntities = filterGroup?.values?.filter(
    entity => entity?.key && keysToToggle.includes(entity.key)
  );
  const nextActive = !matchedEntities?.some(entity => entity?.active);

  matchedEntities?.forEach(entity => {
    if (entity) {
      entity.active = nextActive;
    }
  });

  return { filters };
};

/**
 * Format filters to satisfy backend API
 * Entry : {name: "showtimes.version_detail", values: [{key: "DubbedSt", versionDetail: "DUBBED_ST"}, {key: "Original", versionDetail: "ORIGINAL"}]}
 * Output : [{showtimes.version_detail: ["ORIGINAL"]}] if version original is selected
 */
export const formatForApi = (
  filters?: Filter[] | null
): FormatedFilters | undefined =>
  filters?.reduce((accumulator: FormatedFilters, filter) => {
    const values = filter.values ?? [];
    const name = filter.name ?? '';
    const activeValues = values
      .filter(value => value?.active)
      .map(value => {
        if (name === SHOWTIMES_VERSION) {
          return value?.versionDetail;
        }
        if (name === BOOKING) {
          return value?.key;
        }
        return value?.translation?.tag;
      });
    if (activeValues.length) accumulator.push({ [name]: activeValues });
    return accumulator;
  }, []);

/**
 * This function will merge apiResponseFilters data into originalFilters data.
 * It will mark any filter not in api response as "unavailable".
 * This is mandatory because the API only returns "available filters" without keeping track of currently active filters.
 * We have to keep the original filters and merge the value of them with the return of the api.
 */
export const updateOriginalFilters = (
  originalFilters?: Filter[] | null,
  apiResponseFilters?: Filter[] | null
) =>
  originalFilters?.map(originalFilter => {
    // loop on original filters
    // for each value ...
    const values = originalFilter.values?.map(baseValues => {
      // ... we set the status of the button to unavailable
      const obj = { ...baseValues, unavailable: true };

      // then we look for data available in the new filters
      const apiResponseFilter = apiResponseFilters?.find(
        item => item.name === originalFilter.name
      );

      if (
        apiResponseFilter &&
        apiResponseFilter.values?.find(item => item?.key === baseValues?.key)
      ) {
        // Marking the filter as available
        obj.unavailable = false;
      }

      return obj;
    });

    return { name: originalFilter.name, values: values };
  });

/**
 * We need to set Booking filter on a specific format to match the other facets.
 *
 * Temporarily hidden pending a product decision (ALLOCINE-15086).
 */
export const modifyBookingFilter = (
  filters?: Maybe<ShowtimesFiltersFacets[]>
) => {
  if (!filters) {
    return null;
  }

  // add booking data
  /*
  const bookingFilter = filters.find(item => item?.name === BOOKING);

  if (bookingFilter) {
    bookingFilter.values = [
      { key: BOOKING, label: trans('showtimes.filters.booking') }
    ];
  }
*/

  return filters.filter(item => item?.name !== BOOKING);
};

/**
 * We disable the version filter if there is only one option in it on the application start for UX purpose.
 */
export const movieHasMultipleVersion = (filters?: ShowtimesFiltersFacets[]) => {
  if (!filters) {
    return null;
  }

  // add booking data
  const versionFilter = filters?.find(item => item?.name === SHOWTIMES_VERSION);

  return !!(versionFilter && (versionFilter.values?.length ?? 0) > 1);
};

/**
 * We need to create an array of disabled dates
 */
export const getDisabledDates = (
  rollerDates?: Maybe<string[]>,
  showtimesDates?: Maybe<string[]>
) =>
  rollerDates?.reduce((accumulator: string[], date) => {
    if (!showtimesDates?.find(item => item === date)) {
      accumulator.push(date);
    }

    return accumulator;
  }, []);

export const movieForTracking = (movie?: MovieFromRequest) => ({
  ...(movie ?? {}),
  genres:
    movie?.genres?.reduce<Array<Genre>>(
      (acc, genre) => (genre.tag ? [...acc, genre.tag] : acc),
      []
    ) ?? null
});

export const getUrlHashParams = (hash: string) => {
  const regex = new RegExp(`${hash}([^&]*)`);
  const match = window?.location?.hash?.match(regex);

  if (match && match[1]) {
    return match[1];
  }

  return null;
};

// Build the version groups fed to ShowtimesVersionHolder from the API node.
// The API only splits showtimes by base version (dubbed/local/original); each
// showtime's accessibility tags (subtitled/SME) must be applied here, mirroring
// ShowtimeHelper::buildSimpleShowtimes/buildShowtimesWithTags on the PHP side,
// so the label shown matches the one used by the filter facets.
export const buildShowtimesGroups = (theaterNode: {
  dubbed?: Maybe<PartialShowtime[]>;
  local?: Maybe<PartialShowtime[]>;
  original?: Maybe<PartialShowtime[]>;
}): Record<string, PartialShowtime[]> => {
  const groups: Record<string, PartialShowtime[]> = {};

  (['dubbed', 'local', 'original'] as const).forEach(type => {
    (theaterNode[type] || []).forEach(showtime => {
      const key = getVersionGroupKey(type, showtime.tags || []);
      (groups[key] ||= []).push(showtime);
    });
  });

  Object.values(groups).forEach(group =>
    group.sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? ''))
  );

  return groups;
};

export const getActiveFilters = (
  filtersFromUrl?: string[],
  filterList?: Maybe<ShowtimesFiltersFacets[]> | null
) =>
  filtersFromUrl?.length
    ? filterList?.map(filter => {
        filter.values?.forEach(value => {
          if (
            (filter.name === SHOWTIMES_VERSION &&
              value?.versionDetail &&
              filtersFromUrl.includes(slugify(value.versionDetail))) ||
            (filter.name === BOOKING &&
              value?.key &&
              filtersFromUrl.includes(slugify(value.key))) ||
            (value?.translation?.tag &&
              filtersFromUrl.includes(slugify(value.translation.tag)))
          ) {
            value.active = true;
          }
        });

        return filter;
      })
    : filterList;
